import { action, computed, observable } from 'mobx';
import { computeMetricsSummaries, MetricsGranularity } from '../lib/metricsUtils';
import { ZipApi } from '../lib/ZipApi';
import { DataSyncMetricsBucket, DataSyncMetricsSummary } from '../types/DataSyncMetrics';

const METRICS_CACHE_TTL_MS = 60 * 1000;

interface MetricsQuery {
  trackerId: string;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

interface MetricsQueryCacheEntry {
  summary?: DataSyncMetricsSummary;
  buckets: DataSyncMetricsBucket[];
  fetchedAtMs: number;
}

export class DataSyncMetricsStore {

  @computed
  public get summaries(): Map<string, DataSyncMetricsSummary> {
    return this._summaries;
  }

  @computed
  public get bucketsByDataSync(): Map<string, DataSyncMetricsBucket[]> {
    return this._bucketsByDataSync;
  }

  @computed
  public get loading(): boolean {
    return this._inFlightQueryKeys.size > 0;
  }

  private static getQueryKey(query: MetricsQuery, datasyncId: string): string {
    return `${query.trackerId}:${query.fromEpoch}:${query.toEpoch}:${query.granularity}:${datasyncId}`;
  }

  private static getRequestKey(query: MetricsQuery, datasyncIds: string[]): string {
    const ids = [...datasyncIds].sort().join(',');
    return `${query.trackerId}:${query.fromEpoch}:${query.toEpoch}:${query.granularity}:${ids}`;
  }

  private static isCacheFresh(entry: MetricsQueryCacheEntry | undefined, nowMs: number): boolean {
    if (!entry) {
      return false;
    }

    return (nowMs - entry.fetchedAtMs) < METRICS_CACHE_TTL_MS;
  }
  @observable private _summaries = new Map<string, DataSyncMetricsSummary>();
  @observable private _bucketsByDataSync = new Map<string, DataSyncMetricsBucket[]>();
  @observable private _inFlightQueryKeys = new Map<string, number>();
  @observable private _cacheByQueryKey = new Map<string, MetricsQueryCacheEntry>();
  private _inFlightRequests = new Map<string, Promise<void>>();

  public isLoadingFor(datasyncId: string, query: MetricsQuery): boolean {
    return this._inFlightQueryKeys.has(DataSyncMetricsStore.getQueryKey(query, datasyncId));
  }

  @action
  public clear() {
    this._summaries = new Map();
    this._bucketsByDataSync = new Map();
    this._cacheByQueryKey = new Map();
    this._inFlightQueryKeys = new Map();
    this._inFlightRequests = new Map();
  }

  public async fetchMetrics(
    trackerId: string,
    datasyncIds: string[],
    options?: {
      fromEpoch?: number;
      toEpoch?: number;
      granularity?: MetricsGranularity;
    }
  ): Promise<void> {
    if (!trackerId || datasyncIds.length === 0) {
      return;
    }

    const toEpoch = options?.toEpoch || Math.floor(Date.now() / 1000);
    const fromEpoch = options?.fromEpoch || (toEpoch - 7 * 24 * 60 * 60);
    const granularity = options?.granularity || 'METRICS_GRANULARITY_DAILY';
    const query: MetricsQuery = {
      trackerId,
      fromEpoch,
      toEpoch,
      granularity,
    };
    const nowMs = Date.now();

    const idsToFetch: string[] = [];
    datasyncIds.forEach((datasyncId) => {
      const cacheKey = DataSyncMetricsStore.getQueryKey(query, datasyncId);
      const cached = this._cacheByQueryKey.get(cacheKey);

      if (cached && DataSyncMetricsStore.isCacheFresh(cached, nowMs)) {
        this.updateLatestData(datasyncId, cached.buckets, cached.summary);
      } else {
        idsToFetch.push(datasyncId);
      }
    });

    if (idsToFetch.length === 0) {
      return;
    }

    const requestKey = DataSyncMetricsStore.getRequestKey(query, idsToFetch);
    const existing = this._inFlightRequests.get(requestKey);
    if (existing) {
      return existing;
    }

    const promise = this.runFetch(query, idsToFetch);
    this._inFlightRequests.set(requestKey, promise);
    try {
      await promise;
    } finally {
      this._inFlightRequests.delete(requestKey);
    }
  }

  public getSummary(datasyncId: string, options?: MetricsQuery): DataSyncMetricsSummary | undefined {
    if (options) {
      const cacheKey = DataSyncMetricsStore.getQueryKey(options, datasyncId);
      return this._cacheByQueryKey.get(cacheKey)?.summary;
    }

    return this._summaries.get(datasyncId);
  }

  public getBuckets(datasyncId: string, options?: MetricsQuery): DataSyncMetricsBucket[] {
    if (options) {
      const cacheKey = DataSyncMetricsStore.getQueryKey(options, datasyncId);
      return this._cacheByQueryKey.get(cacheKey)?.buckets || [];
    }

    return this._bucketsByDataSync.get(datasyncId) || [];
  }

  @action
  private markInFlight(query: MetricsQuery, datasyncIds: string[], delta: 1 | -1) {
    datasyncIds.forEach((datasyncId) => {
      const key = DataSyncMetricsStore.getQueryKey(query, datasyncId);
      const next = (this._inFlightQueryKeys.get(key) || 0) + delta;
      if (next <= 0) {
        this._inFlightQueryKeys.delete(key);
      } else {
        this._inFlightQueryKeys.set(key, next);
      }
    });
  }

  private async runFetch(query: MetricsQuery, idsToFetch: string[]): Promise<void> {
    this.markInFlight(query, idsToFetch, 1);
    try {
      const response = await ZipApi.getDataSyncMetrics(
        query.trackerId,
        idsToFetch,
        query.fromEpoch,
        query.toEpoch,
        query.granularity
      );
      const buckets = response.metrics_buckets || [];

      const groupedBuckets = new Map<string, DataSyncMetricsBucket[]>();
      idsToFetch.forEach((datasyncId) => groupedBuckets.set(datasyncId, []));
      buckets.forEach((bucket) => {
        const entries = groupedBuckets.get(bucket.datasync_id) || [];
        entries.push(bucket);
        groupedBuckets.set(bucket.datasync_id, entries);
      });

      const newSummaries = computeMetricsSummaries(buckets, {
        fromEpoch: query.fromEpoch,
        toEpoch: query.toEpoch,
        granularity: query.granularity,
      });

      const fetchedAtMs = Date.now();
      idsToFetch.forEach((datasyncId) => {
        const grouped = groupedBuckets.get(datasyncId) || [];
        const summary = newSummaries.get(datasyncId);

        this.updateLatestData(datasyncId, grouped, summary);
        this.writeCacheEntry(DataSyncMetricsStore.getQueryKey(query, datasyncId), {
          summary,
          buckets: grouped,
          fetchedAtMs,
        });
      });
    } finally {
      this.markInFlight(query, idsToFetch, -1);
    }
  }

  @action
  private writeCacheEntry(cacheKey: string, entry: MetricsQueryCacheEntry) {
    this._cacheByQueryKey.set(cacheKey, entry);
  }

  @action
  private updateLatestData(
    datasyncId: string,
    buckets: DataSyncMetricsBucket[],
    summary?: DataSyncMetricsSummary
  ) {
    this._bucketsByDataSync.set(datasyncId, buckets);
    if (summary) {
      this._summaries.set(datasyncId, summary);
    }
  }
}

export const dataSyncMetricsStore = new DataSyncMetricsStore();
